'use client';
import { useEffect, useState } from 'react';
import { VERSION_OPTIONS, KNOWN_SUBDOMAINS } from '../../../../constants';
import SelectButton from '../../atoms/SelectButton';
import styles from './VersionSelect.module.css';

/**
 * This function extracts the subdomain from the current URL.
 * The URL structure is assumed to be in the format of:
 *   - https://{subdomain}.pathwaysexplorer.climact.com (with a subdomain)
 *   - https://pathwaysexplorer.climact.com (without a subdomain)
 *
 * We check if the hostname has more than two parts (which indicates a subdomain)
 * and return the subdomain (e.g., "latest" or "v2024-h1").
 * If there is no subdomain (i.e., only the domain "pathwaysexplorer.climact.com"),
 * we return "stable" as the default version.
 */
const getSubdomain = () => {
  if (typeof window !== 'undefined') {
    const { hostname } = new URL(window.location.href);
    const hostParts = hostname.split('.');
    const subdomainCandidate = hostParts[0].toLowerCase();
    return KNOWN_SUBDOMAINS.includes(subdomainCandidate) ? subdomainCandidate : 'stable';
  }
  return 'stable'; // Fallback to 'stable'
};

/**
 * Determines the current version based on the subdomain.
 * It looks for a match in VERSION_OPTIONS. If no match is found,
 * the version is considered undefined, and the select button will not be displayed.
 */
const getCurrentVersion = () => {
  const subdomain = getSubdomain();
  return VERSION_OPTIONS.find((option) => option.value === subdomain);
};

const VersionSelect = () => {
  const [currentVersion, setCurrentVersion] = useState(undefined);

  useEffect(() => {
    setCurrentVersion(getCurrentVersion());
  }, []);

  const handleOnChange = (selectedOption) => {
    const pathname = window.location.pathname;
    const newUrl =
      selectedOption.value !== 'stable'
        ? `https://${selectedOption.value}.pathwaysexplorer.climact.com${pathname}`
        : `https://pathwaysexplorer.climact.com${pathname}`; // No subdomain for "stable"

    setCurrentVersion(selectedOption);
    /**
     * Use window.location.assign() to navigate to the new version URL.
     * This method adds a new entry to the browser's history, allowing users to
     * navigate back to the previous page version window.location.replace()
     * which would replace the history entry and prevent going back.
     **/
    window.location.assign(newUrl);
  };

  return (
    currentVersion && (
      <div className={styles.container}>
        <SelectButton
          selectedOption={currentVersion}
          selectOptions={VERSION_OPTIONS}
          handleOnChange={handleOnChange}
          height={20}
          isSearchable={false}
        />
      </div>
    )
  );
};

export default VersionSelect;
